import { colors } from 'fitify-ui'
import { Typography } from 'fitify-ui-landing/src/components/Typography/Typography'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { Section } from '@/components/exercises/Exercise/Section/Section'
import type { ExerciseItem } from '@/utils/exercises'

import {
  StyledColumn,
  StyledColumns,
  StyledList,
  StyledListItem,
  StyledSubsection,
} from './Instructions.Styled'

type InstructionSubsection = {
  title: string
  items: string[]
  bulletColor: string
}

type Props = {
  exercise: ExerciseItem
}

export const Instructions = ({ exercise }: Props) => {
  const { t } = useTranslation()

  const subsections = useMemo<InstructionSubsection[]>(() => {
    return [
      {
        title: t('landing_exercises_breathing'),
        items: exercise.instructions?.breathing || [],
        bulletColor: colors.Green400,
      },
      {
        title: t('landing_exercises_make_harder'),
        items: exercise.instructions?.harder || [],
        bulletColor: colors.Orange400,
      },
      {
        title: t('landing_exercises_make_easier'),
        items: exercise.instructions?.easier || [],
        bulletColor: colors.Yellow400,
      },
    ].filter((section) => section.items.length > 0)
  }, [
    exercise.instructions?.breathing,
    exercise.instructions?.easier,
    exercise.instructions?.harder,
    t,
  ])

  const hasContent = Boolean(
    exercise.instructions?.hints.length || subsections.length
  )

  if (!hasContent) {
    return null
  }

  return (
    <Section title={t('landing_exercises_instructions')}>
      <StyledColumns>
        {exercise.instructions?.hints && (
          <StyledColumn>
            <StyledSubsection>
              <Typography $color={colors.Gray700} $variant="Headline/H 16">
                {t('landing_exercises_tips')}
              </Typography>
              <StyledList>
                {exercise.instructions.hints.map((hint) => (
                  <StyledListItem key={hint} $bulletColor={colors.Blue400}>
                    {t(hint)}
                  </StyledListItem>
                ))}
              </StyledList>
            </StyledSubsection>
          </StyledColumn>
        )}

        {subsections.length > 0 && (
          <StyledColumn>
            {subsections.map((subsection) => (
              <StyledSubsection key={subsection.title}>
                <Typography
                  as="h3"
                  $color={colors.Gray700}
                  $variant="Headline/H 16"
                >
                  {subsection.title}
                </Typography>
                <StyledList>
                  {subsection.items.map((item) => (
                    <StyledListItem
                      key={item}
                      $bulletColor={subsection.bulletColor}
                    >
                      {t(item)}
                    </StyledListItem>
                  ))}
                </StyledList>
              </StyledSubsection>
            ))}
          </StyledColumn>
        )}
      </StyledColumns>
    </Section>
  )
}
